import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTracking } from "react-tracking";
import { trackPromise } from "react-promise-tracker";

// Redux slices and actions
import { updateUserGroup, createUserGroup } from "src/slices/userGroupSlice";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";
import { useDialogBox } from "src/hooks/useDialogBox";
import { useDirtyFormGuard } from "src/hooks/useDirtyFormGuard";

// Layout/UI
import { Box, Stack } from "@metrea-ui/v2";
import FormTextField from "src/components/UI/common/FormTextField";
import FormMultiSelect from "src/components/UI/common/FormMultiSelect";
import RecordNav from "src/components/UI/RecordNav/RecordNav";

// Utilities
import { Session, isDeveloper } from "src/utils/sessionUtils";
import { debugLog, errorLog } from "src/utils/debugUtils";
import { getValuesByPropertyPath } from "src/utils/dataUtils";
import { verifyFields } from "src/utils/formValidationUtils";
import { mColor } from "src/utils/themeUtils";

// Constants
import { initialUserGroupState } from "src/constants/initialStates";

const EditUserGroup = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const dispatch = useDispatch();
	const { trackEvent } = useTracking();

	const pageMode = props.mode;
	const uiSize = props.size;
	const isDisabled = pageMode !== "edit";

	// load data from Redux Selectors || initialUserGroupState (for new group)
	const currUserGroup = useSelector((state) => state.userGroups.selectedGroup) || initialUserGroupState;

	// --- Local State ---
	const [localUserGroup, setLocalUserGroup] = useState(currUserGroup);
	const [users, setUsers] = useState([]);

	const [nameError, setNameError] = useState("");
	const [groupCodeError, setGroupCodeError] = useState("");

	// Custom hooks
	const { allUsers } = useLookupsData();
	const { showDialog, withError } = useDialogBox();

	// Keep the required-field rules shared across initial sync, record changes,
	// input edits, and revert without making the selection-sync effect rerun on every render.
	const validators = useMemo(
		() => [
			{
				field: "name",
				validate: (v) => v !== "",
				setError: setNameError,
				message: "Group name is required!",
			},
			{
				field: "group_code",
				validate: (v) => v !== "",
				setError: setGroupCodeError,
				message: "Group code is required!",
			},
		],
		[]
	);
	const verifyDataFields = useCallback((data) => verifyFields(data, validators), [validators]);

	// FormMultiSelect receives normalized options; selection remains controlled by `userIds`.
	const allUsersForMulti = useMemo(
		() =>
			(allUsers || []).map((u) => ({
				...u,
				// ensure a label so MultiSelect shows email when name is absent
				label: u.name || u.email || u.username || `User #${u.id}`,
			})),
		[allUsers]
	);

	// --- Effect: mount logging
	useEffect(() => {
		debugLog(2, "[EditUserGroup][FLOW] mounted", {});
	}, []);

	// --- Effect: sync local userGroup on selection change ---
	useEffect(() => {
		setLocalUserGroup(currUserGroup);
		const nextUserIds = getValuesByPropertyPath(currUserGroup?.users, "id") || [];
		setUsers(nextUserIds);
		verifyDataFields(currUserGroup);
		debugLog(3, "[EditUserGroup][STATE] synced currUserGroup", {
			id: currUserGroup?.id,
			name: currUserGroup?.name,
		});
	}, [currUserGroup, verifyDataFields]);

	const currentGroupSnapshot = useMemo(
		() => ({
			name: currUserGroup?.name ?? "",
			group_code: currUserGroup?.group_code ?? "",
			comments: currUserGroup?.comments ?? "",
			userIds: getValuesByPropertyPath(currUserGroup?.users, "id") || [],
		}),
		[currUserGroup]
	);

	const localGroupSnapshot = useMemo(
		() => ({
			name: localUserGroup?.name ?? "",
			group_code: localUserGroup?.group_code ?? "",
			comments: localUserGroup?.comments ?? "",
			userIds: getValuesByPropertyPath(localUserGroup?.users, "id") || [],
		}),
		[localUserGroup]
	);

	const hasValidationError = nameError !== "" || groupCodeError !== "";
	const { canSave, canRevert } = useDirtyFormGuard({
		originalValue: currentGroupSnapshot,
		currentValue: localGroupSnapshot,
		hasError: hasValidationError,
	});

	// --- Handlers ---
	const handleInputChange = (event) => {
		const { name, value } = event.target;
		debugLog(3, "[EditUserGroup][ACTION] handleInputChange", { name: name, value: value });

		setLocalUserGroup((prev) => {
			const updateData = {
				...prev,
				[name]: value,
				updatedAt: new Date(),
				updatedBy: Session().currUser.id,
			};
			verifyDataFields(updateData);
			return updateData;
		});
	};

	const handleBackToList = () => {
		debugLog(3, "[EditUserGroup][ACTION] handleBackToList", {});
		props.onReloadList();
	};

	const clickBackToListHandler = (option) => {
		debugLog(3, "[EditUserGroup][ACTION] clickBackToListHandler", { option: option });
		handleBackToList();
	};

	const clickArchiveHandler = (event, status) => {
		event.preventDefault();
		debugLog(3, "[EditUserGroup][ACTION] clickArchiveHandler", {
			status: status,
			groupId: localUserGroup?.id,
		});

		const updateData = {
			...localUserGroup,
			status_id: status,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};
		setLocalUserGroup(updateData);

		const strAction = status === 0 ? "archived" : "re-activated";
		debugLog(2, "[EditUserGroup][FLOW] updateUserGroup started", {
			id: localUserGroup?.id,
			status: status,
			action: strAction,
		});
		dispatch(updateUserGroup({ id: localUserGroup.id, data: updateData }))
			.unwrap()
			.then((response) => {
				debugLog(3, "[EditUserGroup][STATE] clickArchiveHandler updateUserGroup success", {
					updateData: updateData,
					response: response,
				});

				showDialog({
					title: "Successful",
					message: [`The user group was ${strAction} successfully!`],
				});

				trackEvent({
					event: `User Group (#${localUserGroup.id}) was ${strAction}`,
					props: null,
				});
			})
			.catch((error) => {
				errorLog(2, "[EditUserGroup][FLOW] updateUserGroup failed", {
					id: localUserGroup?.id,
					action: strAction,
					error: error,
				});
				console.error(`${strAction} failed`, error);
				withError(error, null, {
					fallbackMessage: `User Group ${strAction} failed.`,
				});
			});
	};

	const clickSaveHandler = (event) => {
		event.preventDefault();
		debugLog(3, "[EditUserGroup][ACTION] clickSaveHandler", {
			groupId: localUserGroup?.id,
			pageMode: pageMode,
		});

		const updateData = {
			...localUserGroup,
			// userAccess: userAccess,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};
		setLocalUserGroup(updateData);

		debugLog(3, "[EditUserGroup][STATE] clickSaveHandler prepared updateData", {
			groupBefore: localUserGroup,
			groupAfter: updateData,
		});

		if (localUserGroup.id) {
			debugLog(2, "[EditUserGroup][FLOW] updateUserGroup started", {
				id: localUserGroup.id,
			});
			trackPromise(
				dispatch(
					updateUserGroup({
						id: localUserGroup.id,
						data: updateData,
					})
				)
					.unwrap()
					.then((response) => {
						debugLog(3, "[EditUserGroup][STATE] clickSaveHandler updateUserGroup success", {
							updateData: updateData,
							response: response,
						});

						showDialog({
							title: "Successful",
							message: ["The user group was updated successfully!"],
						});

						trackEvent({
							event: pageMode === "edit" ? "updateUserGroup" : "updateProfile",
							props: { to: updateData },
						});
					})
					.catch((error) => {
						errorLog(2, "[EditUserGroup][FLOW] updateUserGroup failed", {
							id: localUserGroup?.id,
							error: error,
						});
						console.error("updateUserGroup failed", error);
						// Group saves are also non-idempotent writes, so preserve the shared
						// backend/transport messaging without exposing a retry action here.
						withError(error, null, {
							fallbackMessage: "User Group update failed.",
						});
					})
			);
		} else {
			debugLog(2, "[EditUserGroup][FLOW] createUserGroup started", {
				name: updateData?.name,
				groupCode: updateData?.group_code,
			});
			trackPromise(
				dispatch(createUserGroup({ data: updateData }))
					.unwrap()
					.then((response) => {
						debugLog(3, "[EditUserGroup][STATE] clickSaveHandler createUserGroup success", {
							updateData: updateData,
							response: response,
						});

						showDialog(
							{
								title: "Successful",
								message: ["The user group was created successfully!"],
							},
							() => handleBackToList()
						);

						trackEvent({
							event: "createUserGroup",
							props: { to: response },
						});
					})
					.catch((error) => {
						errorLog(2, "[EditUserGroup][FLOW] createUserGroup failed", {
							name: updateData?.name,
							error: error,
						});
						console.error("createUserGroup failed", error);
						withError(error, null, {
							fallbackMessage: "User Group create failed.",
						});
					})
			);
		}
	};

	const clickRevertHandler = (event) => {
		event?.preventDefault?.();
		debugLog(3, "[EditUserGroup][ACTION] clickRevertHandler", {
			groupId: currUserGroup?.id,
		});

		const nextUserIds = getValuesByPropertyPath(currUserGroup?.users, "id") || [];
		setLocalUserGroup(currUserGroup);
		setUsers(nextUserIds);
		verifyDataFields(currUserGroup);
	};

	const changeUserHandler = (selected) => {
		debugLog(3, "[EditUserGroup][ACTION] changeUserHandler", {
			selectedCount: selected?.length ?? 0,
		});
		const updateData = {
			...localUserGroup,
			arrayUserId: getValuesByPropertyPath(selected, "id"),
			users: selected,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};
		setUsers(updateData.arrayUserId);
		setLocalUserGroup(updateData);

		debugLog(3, "[EditUserGroup][STATE] changeUserHandler updated users", {
			selected: selected,
			groupBefore: localUserGroup,
			groupAfter: updateData,
		});
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[EditUserGroup][FLOW] render", {
		renderCount: renderCount.current,
		pageMode: pageMode,
		localUserGroupId: localUserGroup?.id,
		usersCount: users?.length ?? 0,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	// UserGroup plain-text fields now share the stable direct-v2 helper while
	// MultiSelect and save/dirty behavior remain screen-local.
	const renderTextField = (props) => <FormTextField {...props} size="sm" helperColor={mColor.Danger} />;

	return (
		<div>
			{localUserGroup ? (
				<>
					<RecordNav
						callFrom="User Group"
						showBack
						{...(pageMode === "edit"
							? {
									...(localUserGroup.status_id !== 1
										? { showRestore: true }
										: { showArchive: true }),
									showRevert: true,
									showSave: true,
							  }
							: {})}
						onClickBackToList={clickBackToListHandler}
						onClickArchive={clickArchiveHandler}
						onClickRevert={clickRevertHandler}
						onClickSave={clickSaveHandler}
						disabledRevert={!canRevert}
						disabledSave={!canSave}
						hasError={hasValidationError}
					/>
					<hr />
					<Box mt="1rem" />

					<Stack>
						{renderTextField({
							id: "name",
							name: "name",
							label: "Group Name",
							required: true,
							placeholder: "Group Name",
							value: localUserGroup.name,
							onChange: handleInputChange,
							disabled: isDisabled,
							errorText: nameError,
						})}
						{renderTextField({
							id: "group_code",
							name: "group_code",
							label: "Group Code",
							required: true,
							placeholder: "Group Code",
							value: localUserGroup?.group_code ?? "",
							onChange: handleInputChange,
							disabled: isDisabled,
							errorText: groupCodeError,
						})}

						<Box mt="1rem" mb="0.5rem">
							{/* Metrea v2 MultiSelect (Combobox multiple) */}
							<FormMultiSelect
								type={{ name: "Users" }} // label source for MultiSelect
								mode={pageMode} // enables disabled-state logic inside MultiSelect
								size={uiSize || "small"}
								items={allUsersForMulti} // now includes a 'label' field
								selected={users}
								hideItemId={1}
								hideItemRule={!isDeveloper()}
								onUpdate={changeUserHandler} // receives selected objects; your handler already supports this
							/>
						</Box>
						{renderTextField({
							id: "comments",
							name: "comments",
							label: "Note/Comments",
							placeholder: "Note/Comments",
							value: localUserGroup?.comments ?? "",
							onChange: handleInputChange,
							disabled: isDisabled,
							multiline: true,
							rows: 4,
						})}
					</Stack>
				</>
			) : (
				<div>
					<br />
					<p>Please click on a User Group...</p>
				</div>
			)}
		</div>
	);
};

export default EditUserGroup;
